import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Link as RouterLink } from "react-router-dom";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import {
  focusRingSx,
  rowHoverBg,
} from "@/features/reassistRequests/utils/tokens";
import { REREPO_LABELS, REREPO_REQUEST_PATH } from "../config";
import { REREPO_LOGO_HEIGHT, RerepoLogo } from "./RerepoLogo";

interface Props {
  /** リンクを押したときの処理（スマホのメニューを閉じるのに使う）。 */
  onNavigate?: () => void;
}

/**
 * サイドバーに置く R.E.REPO（不動産調査の依頼）の入口（展開時）。
 * R.E.ASSIST の入口（ReassistSideMenuSection）と同じ形にそろえる。箱に入れず、上の線で他のメニューと区切り、
 * ロゴ（説明はツールチップ）と「不動産調査を依頼する」のメニュー行を置く。
 * すぐ下に R.E.ASSIST の入口が続き、その上の線が区切りになるため、下の線は引かない（引くと線が2本並ぶ）。
 */
export const RerepoSideMenuSection: React.FC<Props> = ({ onNavigate }) => {
  const theme = useTheme();
  const isReadOnly = useReadOnlyUser();
  return (
    <Box
      component="section"
      aria-label={REREPO_LABELS.service}
      sx={{
        mt: 1,
        pt: 2,
        pb: 1,
        borderTop: `1px solid ${theme.palette.border.main}`,
        backgroundColor: theme.palette.white.main,
      }}
    >
      <Box sx={{ px: 2 }}>
        {/* 説明はツールチップで出す。キーボードでも出るよう focus できるようにする */}
        <Tooltip title={REREPO_LABELS.sideMenuTooltip} placement="right" arrow>
          <Box
            tabIndex={0}
            sx={{
              display: "inline-block",
              borderRadius: 0.5,
              ...focusRingSx(theme),
            }}
          >
            <RerepoLogo height={REREPO_LOGO_HEIGHT} />
          </Box>
        </Tooltip>
      </Box>
      <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth>
        <ListItemButton
          component={RouterLink}
          to={REREPO_REQUEST_PATH}
          disabled={isReadOnly}
          onClick={onNavigate}
          sx={{
            mt: 2,
            minHeight: 44,
            px: 2,
            py: 0,
            "&:hover": { backgroundColor: rowHoverBg(theme) },
            ...focusRingSx(theme),
          }}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            <AddIcon aria-hidden sx={{ fontSize: 16, color: "primary.main" }} />
          </ListItemIcon>
          <ListItemText
            primary={REREPO_LABELS.request}
            primaryTypographyProps={{
              fontSize: { xs: 16, [MOBILE_BREAKPOINT]: 14 },
              fontWeight: 700,
              color: "primary.main",
            }}
            sx={{ m: 0 }}
          />
        </ListItemButton>
      </ReadOnlyTooltip>
    </Box>
  );
};
